import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  Building2,
  LogOut,
  Palette,
  ShieldCheck,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ThemePicker } from "@/components/theme-picker";
import { useAuth } from "@/context/auth-context";
import { useOptionalTenant } from "@/context/tenant-context";
import {
  useNotificationPrefs,
  useSetChatToastsEnabled,
} from "@/hooks/use-notification-prefs";

export function AccountSettingsContent({
  showBack = true,
  showTitle = true,
  compactAccess = false,
}: {
  showBack?: boolean;
  showTitle?: boolean;
  compactAccess?: boolean;
}) {
  const { user, signOut, isInstanceAdmin } = useAuth();
  const navigate = useNavigate();
  const tenant = useOptionalTenant();
  const [themePickerOpen, setThemePickerOpen] = useState(false);

  const { data: notifPrefs, isLoading: notifLoading } = useNotificationPrefs();
  const setChatToasts = useSetChatToastsEnabled();
  const chatToastsEnabled = notifPrefs?.chatToastsEnabled !== false;

  const initials = (user?.name || user?.email || "U")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <>
      {(showTitle || showBack) && (
        <div className="mb-6 flex items-center justify-between">
          {showTitle ? (
            <h1 className="text-2xl font-semibold text-text-primary">My account</h1>
          ) : (
            <span />
          )}
          {showBack && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (isInstanceAdmin) {
                  navigate("/dashboard");
                } else if (tenant?.tenantId) {
                  navigate(`/tenants/${tenant.tenantId}/workspace`);
                } else {
                  navigate(-1);
                }
              }}
            >
              <ArrowLeft size={14} className="mr-1.5" />
              Back
            </Button>
          )}
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User size={18} />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent text-lg font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-text-primary">
                {user?.name || "-"}
              </p>
              <p className="text-xs text-text-muted">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck size={18} />
            Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Platform role</span>
            <Badge variant={isInstanceAdmin ? "success" : "default"}>
              {isInstanceAdmin ? "MSP admin" : "Standard user"}
            </Badge>
          </div>
          {!compactAccess && tenant?.tenantName && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Current tenant</span>
              <span className="flex items-center gap-2 text-sm text-text-primary">
                <Building2 size={14} className="text-text-muted" />
                {tenant.tenantName}
              </span>
            </div>
          )}
          {!compactAccess && tenant?.tenantName && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Tenant role</span>
              <Badge
                variant={
                  tenant.userRole === "tenant_admin"
                    ? "success"
                    : tenant.userRole === "agent_admin"
                      ? "warning"
                      : "secondary"
                }
              >
                {tenant.userRole === "tenant_admin"
                  ? "Tenant admin"
                  : tenant.userRole === "agent_admin"
                    ? "Agent admin"
                    : "Standard user"}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette size={18} />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setThemePickerOpen(true)}
          >
            <Palette size={14} className="mr-1.5" />
            Change theme
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell size={18} />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">
                Agent reply alerts
              </p>
              <p className="mt-0.5 text-xs text-text-muted">
                Pop-up toasts when an agent posts a new message. We never show
                one for the chat you're currently viewing.
              </p>
            </div>
            <Switch
              aria-label="Toggle agent reply alerts"
              checked={chatToastsEnabled}
              disabled={notifLoading || setChatToasts.isPending}
              onCheckedChange={(next) => setChatToasts.mutate(next)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <Button size="sm" variant="outline" onClick={handleSignOut}>
            <LogOut size={14} className="mr-1.5" />
            Sign out
          </Button>
        </CardContent>
      </Card>

      {themePickerOpen && (
        <ThemePicker onClose={() => setThemePickerOpen(false)} />
      )}
    </>
  );
}

export function Account() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-8 py-6">
        <AccountSettingsContent />
      </div>
    </div>
  );
}
